import React, { useState } from "react";
import { format, parseISO } from "date-fns";
import { AlertTriangle, CheckCircle2, Clock, Loader2, XCircle } from "lucide-react";
import CustomModal from "../common/CustomModal";
import { formatDisplayDateTime } from "../../utils/helperFunctions";

const time = (iso) => (iso ? format(parseISO(iso), "h:mm a") : "");

const plural = (n, word) => `${n} ${word}${n === 1 ? "" : "s"}`;

function duration(run) {
  if (!run.started_at || !run.finished_at) return "";
  const secs = Math.max(0, Math.round((parseISO(run.finished_at) - parseISO(run.started_at)) / 1000));
  return secs < 60 ? `${secs}s` : `${Math.floor(secs / 60)}m ${secs % 60}s`;
}

/** One line next to the Sync button: what the latest run did, click for the full report. */
const SyncLiveStatus = ({ run }) => {
  const [open, setOpen] = useState(false);
  if (!run) return null;

  const by = run.triggered_by_name ? ` by ${run.triggered_by_name}` : " (auto)";
  const { counts } = run;
  let body;
  if (run.status === "running") {
    body = (
      <span className="flex items-start gap-1.5 text-gray-600">
        <Loader2 className="mt-px w-3.5 h-3.5 shrink-0 animate-spin" />
        Sync running since {time(run.started_at)}{by}
      </span>
    );
  } else if (run.status === "failed" || run.status === "stale") {
    body = (
      <span className="flex items-start gap-1.5 text-red-600">
        <XCircle className="mt-px w-3.5 h-3.5 shrink-0" />
        Last sync {run.status === "stale" ? "did not finish" : "failed"} · {time(run.started_at)}{by}
      </span>
    );
  } else {
    const issues = [
      counts.duplicates && plural(counts.duplicates, "duplicate"),
      counts.accounts_failed && `${plural(counts.accounts_failed, "account")} not read`,
    ].filter(Boolean);
    body = (
      <span className="flex items-start gap-1.5 text-gray-600">
        {issues.length ? (
          <AlertTriangle className="mt-px w-3.5 h-3.5 shrink-0 text-amber-500" />
        ) : (
          <CheckCircle2 className="mt-px w-3.5 h-3.5 shrink-0 text-emerald-600" />
        )}
        Last sync {time(run.finished_at || run.started_at)}{by} · {run.marked_live} marked live
        {issues.length ? ` · ${issues.join(" · ")}` : ""}
      </span>
    );
  }

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="max-w-full text-left text-xs hover:underline underline-offset-2 md:max-w-md md:text-right"
        title="View sync report"
      >
        {body}
      </button>
      {open && <SyncLiveReportModal run={run} onClose={() => setOpen(false)} />}
    </>
  );
};

const Section = ({ icon, title, hint, items, render }) => {
  if (!items?.length) return null;
  return (
    <section className="space-y-2">
      <div>
        <h3 className="flex items-center gap-2 text-sm font-semibold text-gray-800">
          {icon}
          {title} · {items.length}
        </h3>
        {hint && <p className="mt-0.5 text-xs text-gray-500">{hint}</p>}
      </div>
      <ul className="max-h-60 overflow-y-auto divide-y divide-gray-100 rounded-lg border border-gray-100">
        {items.map((item, i) => (
          <li key={i} className="px-3 py-2 text-xs">
            {render(item)}
          </li>
        ))}
      </ul>
    </section>
  );
};

const AdName = ({ children }) => <p className="font-mono text-gray-800 break-all">{children}</p>;

const copyLine = (copy) =>
  [copy.ad_account_name, copy.created_time && `created ${formatDisplayDateTime(copy.created_time)}`, `id ${copy.meta_ad_id}`]
    .filter(Boolean)
    .join(" · ");

const SyncLiveReportModal = ({ run, onClose }) => {
  const report = run.report || {};
  const took = duration(run);
  const withReasons = (report.unmatched_ams || []).some((u) => u.reason);
  const nothing =
    run.status === "success" &&
    !["matched", "duplicates", "ambiguous", "failed", "accounts_failed", "unmatched_ams"].some(
      (k) => report[k]?.length,
    );

  return (
    <CustomModal open title="Sync report" onClose={onClose} isButtonsRequired={false} className="md:!w-[70%] lg:!w-[44%]" bodyClassName="py-4">
      <div className="space-y-5">
        <p className="text-xs text-gray-500">
          {formatDisplayDateTime(run.started_at)}
          {run.triggered_by_name ? ` · by ${run.triggered_by_name}` : " · automatic hourly sync"}
          {took ? ` · took ${took}` : ""}
          {run.status === "success" && run.ad_sets_checked != null
            ? ` · checked ${plural(run.ad_sets_checked, "ad set")} in ${plural(run.accounts_scanned, "account")}`
            : ""}
          {run.status === "success" && run.ad_sets_checked == null
            ? ` · checked ${plural(run.meta_ads_scanned, "live Meta ad")} across ${plural(run.accounts_scanned, "account")}`
            : ""}
        </p>

        {run.status === "running" && (
          <p className="flex items-center gap-2 text-sm text-gray-600">
            <Loader2 className="w-4 h-4 animate-spin" /> Still running. This report fills in when it finishes.
          </p>
        )}
        {run.status === "failed" && (
          <p className="rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700">
            The sync stopped with an error: {run.error_message || "unknown error"}. Try again in a few minutes.
          </p>
        )}
        {run.status === "stale" && (
          <p className="rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700">
            This sync never finished, most likely the worker restarted. Run it again.
          </p>
        )}
        {nothing && <p className="text-sm text-gray-600">Nothing new to sync. Every Ready ad was already matched or is not live on Meta yet.</p>}

        <Section
          icon={<CheckCircle2 className="w-4 h-4 text-emerald-600" />}
          title="Marked live"
          items={report.matched}
          render={(m) => (
            <>
              <AdName>{m.ad_name}</AdName>
              {m.ad_account_name && <p className="mt-0.5 text-gray-500">{m.ad_account_name}</p>}
            </>
          )}
        />
        <Section
          icon={<AlertTriangle className="w-4 h-4 text-amber-500" />}
          title="Duplicates on Meta"
          hint="This ad is live more than once. AMS tracks the first copy; pause or delete the others on Meta or their spend is not tracked."
          items={report.duplicates}
          render={(d) => (
            <>
              <AdName>{d.ad_name}</AdName>
              <p className="mt-1 text-gray-600">Linked: {copyLine(d.linked)}</p>
              {d.others.map((o) => (
                <p key={o.meta_ad_id} className="text-amber-700">Also running, pause this one: {copyLine(o)}</p>
              ))}
            </>
          )}
        />
        <Section
          icon={<XCircle className="w-4 h-4 text-red-500" />}
          title="Accounts that could not be read"
          hint="Ad sets in these accounts were not checked in this run. Use Retry sync on the ad set, or sync again, after the wait."
          items={report.accounts_failed}
          render={(a) => (
            <>
              <p className="font-medium text-gray-800">{a.name || a.id}</p>
              <p className="mt-0.5 text-gray-500 break-all line-clamp-2">
                {a.retry_after_minutes
                  ? `Meta rate limit, try again in about ${a.retry_after_minutes} min`
                  : `Tried ${plural(a.attempts || 1, "time")}: ${a.error}`}
              </p>
            </>
          )}
        />
        <Section
          icon={<AlertTriangle className="w-4 h-4 text-amber-500" />}
          title="Same name on more than one AMS hook"
          hint="Two AMS hooks share this name, so the sync cannot tell which one is live. Mark the right one live by hand."
          items={report.ambiguous}
          render={(a) => <AdName>{a.ad_name}</AdName>}
        />
        <Section
          icon={<XCircle className="w-4 h-4 text-red-500" />}
          title="Matched but not saved"
          items={report.failed}
          render={(f) => (
            <>
              <AdName>{f.ad_name}</AdName>
              <p className="mt-0.5 text-red-600 break-all">{f.error}</p>
            </>
          )}
        />
        {withReasons ? (
          <Section
            icon={<Clock className="w-4 h-4 text-gray-400" />}
            title="Not marked live"
            hint="Each hook shows why. Fix it in Ads Manager, then use Retry sync on the ad set."
            items={report.unmatched_ams}
            render={(u) => (
              <>
                <AdName>{u.ad_name}</AdName>
                <p className="mt-0.5 text-gray-600">{u.reason}</p>
                {u.meta_names?.length > 0 && (
                  <p className="mt-0.5 text-gray-500 break-all">On Meta instead: {u.meta_names.join(", ")}</p>
                )}
              </>
            )}
          />
        ) : (
          <Section
            icon={<Clock className="w-4 h-4 text-gray-400" />}
            title="Ready in AMS, not found live on Meta"
            hint="Not launched yet, still in review on Meta, or launched under a different name. Check Ads Manager before launching again."
            items={report.unmatched_ams}
            render={(u) => <AdName>{u.ad_name}</AdName>}
          />
        )}
      </div>
    </CustomModal>
  );
};

export default SyncLiveStatus;
